import React from 'react';
import {
  ArrowRight,
  CheckCircle2,
  Compass,
  Globe2,
  Layers3,
  Leaf,
  ShieldCheck,
} from 'lucide-react';
import './About.css';

import richardImage from '../../img/PHOTO-2019-03-30-14-33-08.jpg';
import jeanPierreImage from '../../img/IMG_1730.jpg';
import fieldworkImage from '../../img/environment/fieldwork.jpg';

const capabilities = [
  {
    icon: Layers3,
    title: 'Integrated practice',
    text: 'Environmental, engineering and social expertise in one team.',
  },
  {
    icon: Globe2,
    title: 'Rwanda & EAC',
    text: 'Local knowledge with experience across the East African region.',
  },
  {
    icon: ShieldCheck,
    title: 'Safeguards expertise',
    text: 'Work aligned with national requirements and international standards.',
  },
  {
    icon: Leaf,
    title: 'Practical sustainability',
    text: 'Clear solutions that help projects move from assessment to action.',
  },
];

const strengths = [
  'Strong local presence and understanding of the East African context',
  'Experience in energy, infrastructure and basin management projects',
  'Integrated engineering, social and environmental expertise',
  'Trusted stakeholder engagement and community consultation',
  'Reliable delivery under demanding project timelines',
  'Clear communication across technical teams and communities',
];

function About() {
  return (
    <main className="about-page">
      <nav className="about-breadcrumbs" aria-label="Breadcrumb">
        <ol>
          <li><a href="/">Home</a></li>
          <li aria-current="page">About Us</li>
        </ol>
      </nav>

      <section className="about-hero" aria-labelledby="about-hero-heading">
        <div className="about-container about-hero-content">
          <p className="about-eyebrow">Environmental &amp; engineering consultancy</p>
          <h1 id="about-hero-heading">Local insight.<br />International standards.</h1>
          <p className="about-hero-copy">
            Eco Design &amp; Protection Ltd is a multidisciplinary consultancy headquartered in Kigali,
            helping organisations plan and deliver responsible development across Rwanda and the region.
          </p>
          <div className="about-hero-actions">
            <a className="about-primary-link" href="/our-services">
              Explore our services <ArrowRight size={18} />
            </a>
            <a className="about-text-link" href="/contact-us">Talk to our team</a>
          </div>
        </div>
      </section>

      <section className="capability-band" aria-label="Our capabilities">
        <div className="about-container capability-grid">
          {capabilities.map(({ icon: Icon, title, text }) => (
            <article className="capability-card" key={title}>
              <span className="capability-icon"><Icon size={22} /></span>
              <div>
                <h2>{title}</h2>
                <p>{text}</p>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="about-story">
        <div className="about-container story-grid">
          <div className="story-copy">
            <p className="section-kicker">Who we are</p>
            <h2>A multidisciplinary team for complex environmental decisions</h2>
            <p>
              We provide environmental and social assessments, cleaner production advice, solid waste
              management, wastewater treatment design and natural resources management. Our work supports
              public institutions, development partners and private clients from early planning through implementation.
            </p>
            <p>
              Our assignments span roads, renewable energy, gas extraction, hydropower, mining,
              manufacturing, telecommunications, agriculture, irrigation and major infrastructure.
              We combine field evidence, engineering judgement and meaningful consultation to produce
              decisions that are technically sound and practical to implement.
            </p>
          </div>
          <figure className="story-figure">
            <img src={fieldworkImage} alt="Environmental fieldwork in a natural landscape" />
            <figcaption><Compass size={18} /> Evidence gathered where projects meet people and nature.</figcaption>
          </figure>
        </div>
      </section>

      <section className="about-direction" aria-labelledby="direction-heading">
        <div className="about-container">
          <p className="section-kicker section-kicker-light">What guides us</p>
          <h2 id="direction-heading" className="direction-heading">Purpose that shapes every assignment</h2>
          <div className="direction-grid">
            <article className="direction-card">
              <span className="direction-number">01</span>
              <p className="direction-label">Our vision</p>
              <h3>Build a trusted institution for environmental and engineering consultancy.</h3>
              <p>
                We apply science, new technologies and cleaner production methods to solve environmental
                challenges and support sustainable development.
              </p>
            </article>
            <article className="direction-card direction-card-accent">
              <span className="direction-number">02</span>
              <p className="direction-label">Our mission</p>
              <h3>Turn environmental requirements into better project outcomes.</h3>
              <ul>
                <li><CheckCircle2 size={18} /> Evidence-based assessments and design</li>
                <li><CheckCircle2 size={18} /> Inclusive stakeholder engagement</li>
                <li><CheckCircle2 size={18} /> Alignment with national and lender standards</li>
              </ul>
            </article>
          </div>
        </div>
      </section>

      <section className="about-strengths" aria-labelledby="strengths-heading">
        <div className="about-container">
          <div className="section-heading-row">
            <div>
              <p className="section-kicker">Why clients choose us</p>
              <h2 id="strengths-heading">Expertise that works in the field</h2>
            </div>
            <p>We connect global good practice with the realities of each project location.</p>
          </div>
          <div className="strength-grid">
            {strengths.map((strength, index) => (
              <article className="strength-item" key={strength}>
                <span>{String(index + 1).padStart(2, '0')}</span>
                <p>{strength}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="about-leadership" aria-labelledby="leadership-heading">
        <div className="about-container">
          <p className="section-kicker">Leadership</p>
          <div className="section-heading-row leadership-heading-row">
            <h2 id="leadership-heading">Meet the senior team</h2>
            <p>Experienced specialists who guide technical quality and client delivery.</p>
          </div>
          <div className="leadership-grid">
            <article className="leader-card">
              <img src={richardImage} alt="Ngendahayo Richard" />
              <div className="leader-copy">
                <p className="leader-role">Managing Director &amp; Senior Technical Advisor</p>
                <h3>Ngendahayo Richard</h3>
                <p>Specialist in environmental science and technology.</p>
                <a href="/contact-us">Contact the team <ArrowRight size={16} /></a>
              </div>
            </article>
            <article className="leader-card">
              <img src={jeanPierreImage} alt="Kabagema Jean Pierre" />
              <div className="leader-copy">
                <p className="leader-role">Associate Consultant</p>
                <h3>Kabagema Jean Pierre</h3>
                <p>Surveyor and specialist in GIS and resettlement action plans.</p>
                <a href="/contact-us">Contact the team <ArrowRight size={16} /></a>
              </div>
            </article>
          </div>
        </div>
      </section>

      <section className="about-cta">
        <div className="about-container about-cta-inner">
          <div>
            <p className="section-kicker">Start a conversation</p>
            <h2>Planning a project with environmental or social requirements?</h2>
          </div>
          <a className="about-primary-link" href="/contact-us">
            Discuss your project <ArrowRight size={18} />
          </a>
        </div>
      </section>
    </main>
  );
}

export default About;
